import { useRef, useState, type FormEvent } from 'react';
import { V2Alert, V2Button, V2FormField, V2Modal } from '@sakhtar/design-system/react';
import { apiPost, apiPut } from '../../../lib/apiClient.js';
import {
  PURPOSE_LABELS,
  bodyVariables,
  faNum,
  insertVariable,
  previewBody,
  smsLength,
} from './smsModel.ts';
import { smsErrorText } from './smsApi.js';
import type { SmsPurpose, SmsStarter, SmsTemplate, SmsVariable } from './smsTypes.ts';

const BODY_MAX = 700;
const PURPOSES: readonly SmsPurpose[] = ['service', 'transactional', 'marketing'];
const PURPOSE_HINT: Readonly<Record<SmsPurpose, string>> = {
  service: 'یادآوری و پیگیری برای مشتریان فعلی',
  transactional: 'تأیید سفارش و پرداخت',
  marketing: 'تبریک و پیشنهاد؛ فقط با رضایت بازاریابی مشتری',
};

export interface SmsTemplateEditorProps {
  readonly base: string;
  readonly open: boolean;
  /** `null` = a new template. */
  readonly template: SmsTemplate | null;
  readonly starter: SmsStarter | null;
  readonly variables: readonly SmsVariable[];
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

export function SmsTemplateEditor(props: SmsTemplateEditorProps) {
  // The parent remounts this per open (key), so initial state is the draft's source of truth.
  const seed = props.template ?? props.starter;
  const [title, setTitle] = useState(seed?.title ?? '');
  const [body, setBody] = useState(seed?.body ?? '');
  const [purpose, setPurpose] = useState<SmsPurpose>(seed?.purpose ?? 'service');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const length = smsLength(body);
  const unknown = bodyVariables(body).filter((k) => !props.variables.some((v) => v.key === k));
  const editingLocked = props.template?.usedBy.length ? props.template.usedBy.length : 0;

  function addVariable(key: string) {
    const el = bodyRef.current;
    const next = insertVariable(
      body,
      key,
      el?.selectionStart ?? body.length,
      el?.selectionEnd ?? body.length,
    );
    setBody(next.text);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.caret, next.caret);
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!title.trim()) return setError('عنوان قالب را وارد کنید.');
    if (!body.trim()) return setError('متن پیامک را وارد کنید.');
    if (unknown.length > 0) return setError(`متغیر {${unknown[0]}} شناخته‌شده نیست.`);
    setBusy(true);
    setError(null);
    try {
      const payload = { title, body, purpose };
      if (props.template) {
        await apiPut(`${props.base}/api/v1/crm/sms/templates/${props.template.id}`, payload);
      } else {
        await apiPost(`${props.base}/api/v1/crm/sms/templates`, payload);
      }
      props.onSaved();
    } catch (err) {
      setError(smsErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <V2Modal
      open={props.open}
      title={props.template ? 'ویرایش قالب پیامک' : 'قالب پیامک جدید'}
      size="lg"
      onClose={() => (busy ? undefined : props.onClose())}
      footer={
        <>
          <V2Button variant="secondary" onClick={props.onClose} disabled={busy}>
            انصراف
          </V2Button>
          <V2Button variant="primary" type="submit" form="sms-template-form" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره قالب'}
          </V2Button>
        </>
      }
    >
      <form
        id="sms-template-form"
        className="sms-editor"
        onSubmit={(e) => void submit(e)}
        noValidate
      >
        {error ? <V2Alert tone="danger" title={error} /> : null}
        {editingLocked > 0 ? (
          <V2Alert tone="info" title="این قالب در یک روال استفاده می‌شود">
            نوع پیام و متغیرها باید با آن روال سازگار بمانند.
          </V2Alert>
        ) : null}

        <V2FormField label="عنوان قالب" required>
          <input
            className="sk-input"
            value={title}
            maxLength={80}
            placeholder="مثلاً: یادآوری سررسید"
            onChange={(e) => setTitle(e.target.value)}
          />
        </V2FormField>

        <fieldset className="sms-editor__purposes">
          <legend>نوع پیام</legend>
          {PURPOSES.map((p) => (
            <label
              key={p}
              className="sms-editor__purpose"
              data-active={purpose === p ? 'true' : undefined}
            >
              <input
                type="radio"
                name="sms-purpose"
                checked={purpose === p}
                onChange={() => setPurpose(p)}
              />
              <strong>{PURPOSE_LABELS[p]}</strong>
              <small>{PURPOSE_HINT[p]}</small>
            </label>
          ))}
        </fieldset>

        <div className="sms-editor__split">
          <div className="sms-editor__compose">
            <V2FormField label="متن پیامک" required>
              <textarea
                ref={bodyRef}
                className="sk-textarea"
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="متن را بنویسید و از متغیرها استفاده کنید…"
              />
            </V2FormField>
            <div className="sms-editor__vars" role="group" aria-label="افزودن متغیر">
              <span>متغیرها:</span>
              {props.variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className="sms-chip"
                  onClick={() => addVariable(v.key)}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <p className="sms-editor__count" data-warn={length.segments > 1 ? 'true' : undefined}>
              {faNum(length.characters)} نویسه، {faNum(length.segments)} پیامک
              {length.unicode ? ' (فارسی: هر پیامک ۷۰ نویسه)' : ''}
              {length.characters > BODY_MAX ? `، حداکثر ${faNum(BODY_MAX)} نویسه` : ''}
            </p>
          </div>

          <div className="sms-editor__preview" aria-live="polite">
            <span className="sms-editor__preview-label">پیش‌نمایش با داده‌ٔ نمونه</span>
            <div className="sms-bubble">
              {body.trim()
                ? previewBody(body, props.variables)
                : 'متن پیامک اینجا نمایش داده می‌شود'}
            </div>
          </div>
        </div>
      </form>
    </V2Modal>
  );
}
